export const carpData = {
  updatedDate: 'June 2026',
  introduction: {
    overview: [
      'The Town of Brookline has long recognized the urgent need to respond to the challenges of climate change. Community members, civic leaders, and Town staff have consistently voiced strong interest in advancing climate action, reflecting a deep commitment to protecting both present and future generations.',
      'The Climate Action and Resiliency Plan (CARP) provides a comprehensive roadmap for reducing greenhouse gas emissions and building resilience to climate impacts across all sectors of the community. It addresses both mitigation — reducing the emissions that drive climate change — and adaptation — preparing for and responding to its effects.',
      'The CARP was developed through extensive community engagement, technical analysis, and coordination with Town departments. It builds on Brookline’s history of climate leadership and establishes measurable goals, strategies, and actions through 2036.'
    ],
    equity: [
      'The CARP centers equity and environmental justice throughout its goals, strategies, and actions. Climate change disproportionately affects vulnerable populations, including low-income residents, communities of color, elderly residents, and those with pre-existing health conditions.',
      'In Brookline, approximately 1 in 4 residents is financially vulnerable, and 1 in 3 spends more than 30% of their income on housing. The plan prioritizes strategies that reduce cost burdens, improve public health, and ensure that the benefits of climate action reach all residents.'
    ],
    framework: [
      'The CARP Implementation Plan is organized around five categories, each addressing a key sector of climate action. Within each category, specific goals define measurable objectives, strategies outline the programs, policies, and projects to achieve those goals, and actions identify the discrete steps within each strategy.',
      'The five categories are: Buildings and Energy (BE), Resource Conservation and Management (RCM), Sustainable Transportation and Mobility (STM), Natural Systems and Community Well-Being (NSCW), and Resilience, Adaptation and Risk Reduction (RAR).',
      'Each strategy includes a Priority Score (1 = highest priority, 3 = lower priority), GHG Reduction Role, Co-Benefits, Partnering Department, Start By year, Cost Estimate ($ = low, $$ = medium, $$$ = high), and Staff Estimate (1 = existing scope, 2 = reallocation needed, 3 = new hire needed).'
    ]
  },
  crossCuttingStrategy: {
    id: 'CC',
    title: 'Climate Revolving Fund',
    description: 'Establish a Climate Revolving Fund to finance climate-related projects, with returns reinvested into future climate initiatives.',
    priorityScore: 1,
    ghgRole: 'Indirect',
    startBy: '2029',
    costEstimate: '$',
    staffEstimate: 2,
    actions: [
      { id: 'CC.a', description: 'Investigate allowable revenues and funding mechanisms for a Climate Revolving Fund.' },
      { id: 'CC.b', description: 'Amend relevant bylaws to authorize the creation and operation of the fund.' },
      { id: 'CC.c', description: 'Seed the Climate Revolving Fund with initial capital and establish governance and reporting structures.' }
    ]
  },
  categories: [
    {
      id: 'BE',
      name: 'Buildings and Energy',
      description: 'Goals and strategies in this category address how buildings are constructed, powered, operated, renovated and deconstructed to reduce GHG emissions.',
      goals: [
        {
          id: 'BE 1',
          title: 'Maximize renewable and resilient electricity for municipal operations by 2036.',
          climateImpact: 'This goal targets a direct and controllable source of emissions by decarbonizing the electricity used for municipal operations, where the Town has full authority over energy procurement and infrastructure decisions. Expanding on-site renewable generation, storage, and a campus microgrid also strengthens resilience, ensuring essential public services remain operational during climate-driven grid disruptions.',
          strategies: [
            {
              id: 'BE 1.1',
              title: 'Pilot a geothermal utility district for Town-owned facilities in unison with capital improvement projects.',
              priorityScore: 2,
              ghgRole: 'Direct, 510 MT CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 1.1.a', description: 'Conduct a feasibility study to identify the most suitable ownership and financing model, along with appropriate municipal sites for district geothermal systems, such as the Brookline High School.' },
                { id: 'BE 1.1.b', description: 'In partnership with utilities and state agencies, design a pilot geothermal utility district.' },
                { id: 'BE 1.1.c', description: 'Secure all necessary permitting and funding for the pilot geothermal utility district based on feasibility study.' },
                { id: 'BE 1.1.d', description: 'Construct and install pilot geothermal utility district.' },
                { id: 'BE 1.1.e', description: 'Study the effectiveness of the pilot geothermal utility district and develop a scalable model for future expansion to other municipal and community sites.' }
              ]
            },
            {
              id: 'BE 1.2',
              title: 'Maximize solar + battery energy installations on public property.',
              priorityScore: 1,
              ghgRole: 'Direct, 770 MT CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$$$',
              staffEstimate: 2,
              actions: [
                { id: 'BE 1.2.a', description: 'Complete a solar siting and capacity assessment of all municipal properties, including rooftops, parking lots, and ground-mounted solar.' },
                { id: 'BE 1.2.b', description: 'Develop a prioritized municipal solar implementation plan, based on the solar siting and capacity assessment, zoning, site space, and other feasibility criteria.' },
                { id: 'BE 1.2.c', description: 'Develop and apply a standardized life-cycle cost-benefit analysis tool to determine whether the solar project should pursue a PPA or municipal purchase.' },
                { id: 'BE 1.2.d', description: 'Design municipal Distributed Energy Resources (DER) whenever feasible in collaboration with the utility.' }
              ]
            },
            {
              id: 'BE 1.3',
              title: 'Consider a municipal microgrid to increase the resiliency of critical infrastructure during peak demand on the electrical grid.',
              priorityScore: 2,
              ghgRole: 'Medium Potential',
              coBenefits: ['Economic'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 1.3.a', description: 'Assess the feasibility and cost of a municipal microgrid serving critical infrastructure.' },
                { id: 'BE 1.3.b', description: 'Based on the feasibility assessment, design an integrated system (geothermal, solar, storage), into a microgrid to reduce carbon emissions and improve resilience for critical Town facilities.' },
                { id: 'BE 1.3.c', description: 'Secure all necessary permitting and funding; upon confirmation of feasibility and funding, proceed with microgrid implementation.' }
              ]
            },
            {
              id: 'BE 1.4',
              title: 'Expand renewable electricity procurement and development for Municipal Operations.',
              priorityScore: 3,
              ghgRole: 'Direct, 2,380 MT CO₂e reduced',
              coBenefits: ['Infrastructure Resilience', 'Economic'],
              partneringDept: 'Purchasing, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 2,
              actions: [
                { id: 'BE 1.4.a', description: 'Research and expand renewable energy program options for municipal accounts, including but not limited to the purchase of MA Class I renewable energy credits (RECs) and virtual power plants (VPPs).' }
              ]
            }
          ]
        },
        {
          id: 'BE 2',
          title: 'Cut gas emissions from municipal buildings (Scope 1) and electricity from streetlights (Scope 2) by 60% by 2036.',
          climateImpact: 'This goal targets a direct and controllable source of emissions by reducing energy use in municipal buildings and electricity demand from streetlights, where the Town has authority over building systems, lighting infrastructure, and capital investments.',
          strategies: [
            {
              id: 'BE 2.1',
              title: 'Prioritize electrification of municipal buildings executing the Municipal Decarbonization Roadmap.',
              priorityScore: 2,
              ghgRole: 'Direct, 599 MT CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 2,
              actions: [
                { id: 'BE 2.1.a', description: 'Conduct building energy assessments.' },
                { id: 'BE 2.1.b', description: 'Seek grant and municipal funding to implement the prioritized actions identified in the Municipal Decarbonization Roadmap.' },
                { id: 'BE 2.1.c', description: 'Establish dedicated decarbonization capital funding in the Capital Improvement Program (CIP) for municipal building electrification.' }
              ]
            },
            {
              id: 'BE 2.2',
              title: 'Reduce energy use intensity (EUI) in municipal buildings by 33% (from 2023 by 2036).',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$',
              staffEstimate: 1,
              actions: [
                { id: 'BE 2.2.a', description: 'Complete remaining building energy audits and assess the cost and benefits of recommended energy efficiency projects.' },
                { id: 'BE 2.2.b', description: 'Continue to track EUI in municipal buildings and annually report through the Green Communities Program.' },
                { id: 'BE 2.2.c', description: 'Reduce energy demand and save money by utilizing Eversource’s smart meters as available.' },
                { id: 'BE 2.2.d', description: 'Apply for grants to complete selected interventions.' }
              ]
            },
            {
              id: 'BE 2.3',
              title: 'Reduce the cost and use of electricity in streetlights.',
              priorityScore: 3,
              ghgRole: 'Direct, 231 MT CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 1,
              actions: [
                { id: 'BE 2.3.a', description: 'Coordinate with Eversource to retrofit streetlights with smart controls that enable dimming and adaptive lighting capabilities.' },
                { id: 'BE 2.3.b', description: 'Track energy savings and community feedback on the pilot streetlight dimming schedule program.' },
                { id: 'BE 2.3.c', description: 'Adjust dimming schedules for optimal performance.' }
              ]
            }
          ]
        },
        {
          id: 'BE 3',
          title: 'Cut emissions from residential and commercial buildings 62% by 2036.',
          climateImpact: 'This goal represents a critical component of the Town’s mitigation strategy, as buildings are the primary source of GHG emissions. Advancing building decarbonization also strengthens resilience by improving indoor air quality, creating healthier and more comfortable homes during periods of extreme heat, and reducing long-term energy cost burdens — particularly for EJ communities.',
          strategies: [
            {
              id: 'BE 3.1',
              title: 'Implement policy changes and incentives that increase energy efficiency and reduce fossil-fuel dependency in residential and commercial buildings.',
              priorityScore: 2,
              ghgRole: 'Indirect, Estimated 34,150 MT CO₂e reduced',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Planning & Community Development, Sustainability & Natural Resources, Town Counsel',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 3.1.a', description: 'Develop a zoning bylaw that requires or incentivizes new construction and major renovations to be solar-equipped or solar-ready, including provisions for battery readiness and necessary firewalls.' },
                { id: 'BE 3.1.b', description: 'Explore developing a zoning bylaw that decreases electric grid-provided energy consumption in new construction and major renovation through requiring or incentivizing energy efficiency measures such as on-site tree planting, cool roofs, rooftop solar, geothermal, passive house certification, mass timber construction, and/or green roofs.' },
                { id: 'BE 3.1.c', description: 'Promote Mass Save energy assessment and efficiency programs, meeting annual goals as detailed in the Community First Partnership grant.' },
                { id: 'BE 3.1.d', description: 'Promote participation in the Property Assessed Clean Energy program, Energy Saver Home Loan Program, and other financing programs that offer low-cost, long-term financing on energy efficiency, weatherization, renewable energy, and/or water conservation projects.' }
              ]
            },
            {
              id: 'BE 3.2',
              title: 'Implement policy changes, incentives, and programs that support the decarbonization of residential and commercial buildings.',
              priorityScore: 2,
              ghgRole: 'Indirect, Estimated 88,550 MT CO₂e',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Planning & Community Development, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 3.2.a', description: 'Promote Mass Save decarbonization programs, meeting annual goals as detailed in the Community First Partnership grant, and promote additional incentives such as electric lawn and home appliance rebates.' },
                { id: 'BE 3.2.b', description: 'Support bulk purchasing programs, collect contractor information, and launch a website offering technical assistance and resources to accelerate electrification measures in private buildings.' },
                { id: 'BE 3.2.c', description: 'Offer technical assistance that supports residential and commercial decarbonization, by connecting property owners with state/federal incentives, hosting office hours with Town departments for representatives to answer questions from contractors and homeowners, and exploring local incentives.' },
                { id: 'BE 3.2.d', description: 'Explore promoting and incentivizing decarbonization measures for residents and businesses through Town funding mechanisms such as the Community Preservation Act (CPA) and the Affordable Housing Trust Funds.' },
                { id: 'BE 3.2.e', description: 'Develop and implement a Town-wide initiative to provide personalized guidance to residents looking to adopt clean energy technologies, such as solar panels and heat pumps, and electrify their homes, including relevant incentive programs.' },
                { id: 'BE 3.2.f', description: 'Collaborate with local leaders to engage the entire Brookline community, especially environmental justice populations, in energy transition planning, including the benefits and incentives of going all-electric.' }
              ]
            },
            {
              id: 'BE 3.3',
              title: 'Develop a Brookline Building Emissions Reduction and Disclosure Initiative (BERDI) to reduce GHG emissions by requiring and supporting existing large, commercial buildings to track, report, and meet declining emissions targets.',
              priorityScore: 2,
              ghgRole: 'Direct, High Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Planning & Community Development, Sustainability & Natural Resources, Town Counsel',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 3.3.a', description: 'Evaluate peer municipal building performance standards, assess local building stock and emissions data, and engage key stakeholders to identify opportunities, barriers, and equity considerations for a phased implementation of a building performance standard in Brookline.' },
                { id: 'BE 3.3.b', description: 'Conduct a financial analysis of the associated impacts of a building performance standard in Brookline.' },
                { id: 'BE 3.3.c', description: 'Draft a building emissions reduction and disclosure framework that defines qualifying buildings, reporting requirements, emissions targets and timelines, and compliance pathways.' },
                { id: 'BE 3.3.d', description: 'Coordinate with Town leadership, elected officials, and relevant boards and commissions to refine the proposed bylaw, develop a phased implementation plan, and support warrant article development and adoption.' }
              ]
            },
            {
              id: 'BE 3.4',
              title: 'Communicate with state agencies, utilities, and local partners engaged with the transition from gas and the replacement or retirement of natural gas pipelines to identify opportunities for, and support, non-pipe alternatives and neighborhood electrification.',
              priorityScore: 3,
              ghgRole: 'Indirect, High Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 3.4.a', description: 'Stay informed on Brookline’s gas infrastructure replacement schedule (through the Gas System Enhancement Plan), and encourage gas and electric utilities to identify opportunities to retire or repair, rather than replace, selected mains.' },
                { id: 'BE 3.4.b', description: 'Support piloting neighborhood targeted electrification zones in partnership with utilities.' },
                { id: 'BE 3.4.c', description: 'Engage local advocacy organizations, community groups, and real estate owners and developers to identify candidate neighborhoods for coordinated electrification, align building retrofit timelines with utility planning, and build support for non-pipe alternatives to gas infrastructure replacement.' },
                { id: 'BE 3.4.d', description: 'Provide public comment, as needed and appropriate, on MA DPU proceedings that affect the Town’s ability to achieve its net-zero carbon emissions goals.' }
              ]
            }
          ]
        },
        {
          id: 'BE 4',
          title: 'Increase renewable energy sourcing for residential and commercial buildings from 77% to 85% by 2030 and 95% by 2036.',
          climateImpact: 'This goal addresses emission reductions from electricity used in residential and commercial buildings by increasing the share of power supplied from renewable sources.',
          strategies: [
            {
              id: 'BE 4.1',
              title: 'Continue offering Brookline’s Community Choice Aggregation (CCA) program to stabilize electricity costs and decrease non-renewable energy use.',
              priorityScore: 1,
              ghgRole: 'Indirect, Estimated 32,800 CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$',
              staffEstimate: 1,
              actions: [
                { id: 'BE 4.1.a', description: 'Conduct annual outreach campaigns to educate residents and businesses that become eligible for enrollment in Brookline’s CCA options, emphasizing the environmental and cost-benefits of choosing higher renewable content.' },
                { id: 'BE 4.1.b', description: 'Conduct an outreach campaign to encourage existing customers to opt up to Brookline ‘All Green’, thereby increasing the amount of renewable energy on the grid.' },
                { id: 'BE 4.1.c', description: 'Update the existing CCA plan to comply with DPU guidelines and increase local autonomy and plan flexibility.' }
              ]
            },
            {
              id: 'BE 4.2',
              title: 'Expand access to low-cost renewable electricity.',
              priorityScore: 2,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Purchasing, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$',
              staffEstimate: 2,
              actions: [
                { id: 'BE 4.2.a', description: 'Partner with existing community solar developers to secure subscription opportunities for residents and businesses, prioritizing projects that offer below-market rates and serve renters, small businesses, and income-eligible households.' },
                { id: 'BE 4.2.b', description: 'Conduct educational outreach about community solar subscriptions that focuses on renters, small businesses, and income-eligible households.' },
                { id: 'BE 4.2.c', description: 'Pursue long-term Virtual Power Purchase Agreements, with large-scale renewable energy projects, such as regional wind or solar farms, to secure RECs and/or enable the financing of new clean energy projects.' },
                { id: 'BE 4.2.d', description: 'Conduct a feasibility study to establish a Sustainable Energy Utility modeled on Ann Arbor, Michigan’s approach, which would provide opt-in, municipally-financed rooftop solar, battery storage, and efficiency services that supplement the existing utility grid without requiring infrastructure acquisition.' }
              ]
            },
            {
              id: 'BE 4.3',
              title: 'Expand and incentivize on-site solar by creating a program to identify underutilized space for solar and give residential and commercial owners technical assistance with overcoming lack of upfront capital and technical obstacles.',
              priorityScore: 1,
              ghgRole: 'Indirect, High Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience', 'Public Health'],
              partneringDept: 'Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$$',
              staffEstimate: 2,
              actions: [
                { id: 'BE 4.3.a', description: 'Investigate on-site rooftop solar initiatives and programs implemented by neighboring municipalities.' },
                { id: 'BE 4.3.b', description: 'Pursue the identified solar initiative or program to assist community members in adding on-site solar to commercial properties, homes, and balconies.' }
              ]
            }
          ]
        },
        {
          id: 'BE 5',
          title: 'Reduce embodied carbon in all new municipal capital projects by 50% by 2036 vs an established reporting baseline, through the use of low-carbon construction materials and practices.',
          climateImpact: 'Targeting embodied carbon reduces GHG emissions that occur during the construction of new buildings and major renovations, before those buildings are occupied or using energy.',
          strategies: [
            {
              id: 'BE 5.1',
              title: 'Develop policies and programs that promote the salvage and reuse of building materials in municipal capital projects.',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Infrastructure Resilience'],
              partneringDept: 'Building, Purchasing, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 5.1.a', description: 'Conduct a baseline analysis to establish existing embodied carbon in Town buildings and from current materials procurement practices.' },
                { id: 'BE 5.1.b', description: 'Establish embodied carbon targets in alignment with the federal Buy Clean Initiative.' },
                { id: 'BE 5.1.c', description: 'Develop Town procurement rules for capital projects that incentivize reduced-carbon materials.' },
                { id: 'BE 5.1.d', description: 'Evaluate the costs and benefits of scaling up a reporting requirement for renovations.' }
              ]
            }
          ]
        },
        {
          id: 'BE 6',
          title: 'Reduce embodied carbon in new private construction and major renovation projects.',
          climateImpact: 'Targeting embodied carbon reduces GHG emissions that occur during the construction of new buildings and major renovations.',
          strategies: [
            {
              id: 'BE 6.1',
              title: 'Gather data on embodied carbon in new private construction and major renovation projects through plan development review, and consider programs and policies to require or incentivize GHG emissions reductions.',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Planning & Community Development, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 6.1.a', description: 'Collaborate with a developer to conduct a Life Cycle Assessment for a major construction project, and determine the cost, effort and expertise required to prepare the assessment.' },
                { id: 'BE 6.1.b', description: 'Update the Site Design Standards (Harvard Street Zoning) and/or Section 5.09 Design Review for Major Impact Projects to require a Life Cycle Assessment, including an analysis of embodied and operational carbon, for construction projects and renovations that have at least 45,000 square feet or 50,000 square feet of gross floor area.' },
                { id: 'BE 6.1.c', description: 'Using the submitted Life Cycle Assessment data, explore requiring or incentivizing GHG emissions reductions in connection with new construction and major renovations through zoning policies.' },
                { id: 'BE 6.1.d', description: 'Advance material reuse by providing clear guidance, small incentives, and public recognition to contractors, homeowners, and developers who prioritize salvage over disposal.' }
              ]
            }
          ]
        },
        {
          id: 'BE 7',
          title: 'Increase electrical grid capacity to ensure Brookline can meet its electrification and distributed energy resource (DER) needs.',
          climateImpact: 'This goal supports climate mitigation and resilience by ensuring the electric grid can accommodate increased electrification and the integration of distributed energy resources as fossil fuel use declines.',
          strategies: [
            {
              id: 'BE 7.1',
              title: 'Coordinate and communicate with utility companies, the DPU, the Massachusetts Grid Modernization Advisory Council (GMAC), and other relevant State agencies and organizations about advancing electrification.',
              priorityScore: 3,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Public Health', 'Economic'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 3,
              actions: [
                { id: 'BE 7.1.a', description: 'Participate in GMAC, DPU, and other proceedings and communications by providing local data, submitting comments, and advocating for grid upgrades, interconnection improvements, neighborhood electrification, and non-pipe alternatives that align with Brookline’s electrification and resilience goals.' },
                { id: 'BE 7.1.b', description: 'Advocate for grid modernization investments, including undergrounding lines and transformers, adding redundancy, and expanding substation capacity to ensure reliable service and promote Brookline’s electrification and resilience goals.' },
                { id: 'BE 7.1.c', description: 'Collaborate with Eversource and National Grid on grid planning and pilot projects, including non-pipeline alternatives and utility programs that integrate solar, battery storage, and microgrids.' },
                { id: 'BE 7.1.d', description: 'Pursue state and federal funding to support DER deployment and grid-interconnection readiness.' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'RCM',
      name: 'Resource Conservation and Management',
      description: 'Goals and strategies in this category target emissions from consumption and waste through diversion, composting, reuse, repair, and circular economy strategies.',
      goals: [
        {
          id: 'RCM 1',
          title: 'By 2036, reduce municipal solid waste disposal by 25% (from approximately 8,000 to 6,000 tons per year) and triple organic waste diversion to at least 1,500 tons annually.',
          climateImpact: 'This goal addresses climate impacts from the disposal of solid waste, where incineration contributes to GHG emissions through direct emissions and fossil fuel intensive processing. Reducing disposal, expanding organics diversion, and prioritizing reuse and upstream waste reduction lowers emissions associated with material extraction, production, and waste handling.',
          strategies: [
            {
              id: 'RCM 1.1',
              title: 'Develop and implement equitable programs through which residents can divert organics, textiles, household hazardous waste, electronics, and other materials from the waste stream.',
              priorityScore: 1,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Equity', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources, Brookline Public Schools',
              startBy: '2029',
              costEstimate: '$',
              staffEstimate: 1,
              actions: [
                { id: 'RCM 1.1.a', description: 'Expand the resident drop-off offerings at the Brookline transfer station and other locations across Town to collect additional hard-to-recycle materials such as, but not limited to, books, clothing, housewares, and sharps.' },
                { id: 'RCM 1.1.b', description: 'Perform regular waste audits to track the composition of the waste stream. Utilize results to guide program development that maximizes opportunities for waste diversion.' },
                { id: 'RCM 1.1.c', description: 'Coordinate with Brookline Public Schools, local businesses, and community-based organizations to donate or upcycle edible food through food rescue and redistribution programs.' }
              ]
            },
            {
              id: 'RCM 1.2',
              title: 'Increase awareness around, and participation in, deconstruction and building material reuse practices.',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Planning & Community Development, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 3,
              actions: [
                { id: 'RCM 1.2.a', description: 'Pilot a deconstruction initiative, in collaboration with a non-profit, that provides residents and developers with deconstruction resources, including technical assistance for demolition and renovation projects.' },
                { id: 'RCM 1.2.b', description: 'Identify opportunities to integrate waste reporting, building material reuse and deconstruction practice into key permitting touchpoints or milestones, such as Design Review and Demolition Permitting.' },
                { id: 'RCM 1.2.c', description: 'Participate in regional networks and working groups that are currently leading efforts to convene representatives from the building industry to understand their workforce capability and resource needs to incorporate building deconstruction practice into their projects.' }
              ]
            },
            {
              id: 'RCM 1.3',
              title: 'Promote recycling and composting programs through equitable outreach and education.',
              priorityScore: 2,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Equity', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$',
              staffEstimate: 1,
              actions: [
                { id: 'RCM 1.3.a', description: 'Create a toolkit outlining best practices to educate residents and property owners of multi-unit buildings about recycling and composting.' },
                { id: 'RCM 1.3.b', description: 'Implement a Compost Ambassador Program that utilizes community volunteers to educate residents in vulnerable and/or hard-to-reach communities about Town compost programs and initiatives.' },
                { id: 'RCM 1.3.c', description: 'Further develop educational materials on organics diversion. Consider organizing a Town-wide mailing to publicize the Town’s composting programs.' },
                { id: 'RCM 1.3.d', description: 'Consider updating Zoning Bylaw Article 5.09 Design Review to require the review of trash/recycling/organic waste storage for new construction.' }
              ]
            },
            {
              id: 'RCM 1.4',
              title: 'Encourage businesses to reduce waste through reuse, composting, and improved recycling operations in both the front- and back-of-house.',
              priorityScore: 3,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'RCM 1.4.a', description: 'Conduct a focus group with local business owners to identify barriers to composting and recycling, and how the Town can better support the business community as it pertains to sustainable waste management.' },
                { id: 'RCM 1.4.b', description: 'Develop case studies of local businesses that have successfully incorporated waste diversion and waste reduction strategies into their day-to-day operations.' },
                { id: 'RCM 1.4.c', description: 'Review tools and strategies utilized by peer municipalities to incentivize business waste reduction and explore funding opportunities to implement as appropriate.' },
                { id: 'RCM 1.4.d', description: 'Create educational resources and execute a targeted education campaign to encourage businesses to divert organics and/or improve recycling operations.' },
                { id: 'RCM 1.4.e', description: 'Consider organizing an outreach campaign to commercial properties to increase compliance with existing local bylaws and state waste bans.' }
              ]
            },
            {
              id: 'RCM 1.5',
              title: 'Evaluate the Town’s solid waste collection program for opportunities to encourage waste reduction and diversion.',
              priorityScore: 3,
              ghgRole: 'Indirect, High Potential',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'RCM 1.5.a', description: 'Conduct a feasibility and cost assessment of transitioning to a true Pay-As-You-Throw program, in which Town Waste Collection Service customers pay a per unit fee for the trash they generate.' },
                { id: 'RCM 1.5.b', description: 'Expand the Town’s solid waste collection program to include optional curbside collection of organic waste.' }
              ]
            },
            {
              id: 'RCM 1.7',
              title: 'Implement programs and policies to encourage municipal departments to divert materials from landfill or incineration and reduce upstream waste generation.',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Purchasing, Sustainability & Natural Resources, Brookline Public Schools',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 2,
              actions: [
                { id: 'RCM 1.7.a', description: 'Amend the Town’s existing Environmentally Preferable Procurement Policy to strengthen the Town’s commitment to purchasing environmentally preferable products and encourage reuse and donation of goods and equipment.' },
                { id: 'RCM 1.7.b', description: 'Develop and launch an organics diversion program in municipal office buildings.' },
                { id: 'RCM 1.7.c', description: 'Expand the school composting program to include composting in all school cafeterias.' }
              ]
            },
            {
              id: 'RCM 1.8',
              title: 'Incorporate consumption-based GHG emissions into community-wide planning and decision-making.',
              priorityScore: 3,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Purchasing, Sustainability & Natural Resources, Brookline Public Schools',
              startBy: '2035',
              costEstimate: '$',
              staffEstimate: 2,
              actions: [
                { id: 'RCM 1.8.a', description: 'Develop a consumption-based GHG emissions inventory to complement the Town’s existing community-wide inventory, capturing emissions associated with goods, food, and services consumed by residents and businesses.' },
                { id: 'RCM 1.8.b', description: 'Identify key consumption categories (e.g., food, construction materials, consumer goods) that contribute most significantly to emissions and prioritize opportunities for reduction.' },
                { id: 'RCM 1.8.c', description: 'Use findings from the consumption-based inventory to inform future initiatives related to waste reduction, procurement, circular economy initiatives, and resident outreach.' },
                { id: 'RCM 1.8.d', description: 'Incorporate consumption-based emissions into future GHG emissions inventories.' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'STM',
      name: 'Sustainable Transportation and Mobility',
      description: 'Goals and strategies within this category seek to reduce emissions from the movement of people and goods throughout Brookline by promoting mode shift, vehicle electrification, fleet decarbonization, and transportation demand management.',
      goals: [
        {
          id: 'STM 1',
          title: 'Electrify 48% of the light-duty vehicle municipal fleet, except for mission-critical vehicles that lack viable electric alternatives, by 2036.',
          climateImpact: 'This goal reduces climate impacts from municipal transportation by shifting vehicle operations away from fossil fuel use and toward electricity.',
          strategies: [
            {
              id: 'STM 1.1',
              title: 'Implement an EV-first approach for the Town-owned municipal fleet, complemented by the necessary EV charging infrastructure to phase out petroleum-powered vehicles over time.',
              priorityScore: 1,
              ghgRole: 'Direct, 1,118 MT CO₂e reduced',
              coBenefits: ['Economic', 'Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Highway & Sanitation, Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$$',
              staffEstimate: 1,
              actions: [
                { id: 'STM 1.1.a', description: 'Develop a municipal fleet electrification plan to convert existing petroleum-powered vehicles to viable electric alternatives.' },
                { id: 'STM 1.1.b', description: 'Secure funding to implement the municipal fleet electrification plan.' },
                { id: 'STM 1.1.c', description: 'Expand municipal EV charging infrastructure to meet fleet demand, while ensuring resilience during natural hazards and power outages.' },
                { id: 'STM 1.1.d', description: 'Explore mobile EV charging options to increase fleet EV adoption and charging resilience in areas where hard-wired stations are not feasible.' },
                { id: 'STM 1.1.e', description: 'Continually monitor non-petroleum alternatives for mission-critical vehicles and large vehicles that do not currently have a viable electric alternative.' }
              ]
            }
          ]
        },
        {
          id: 'STM 2',
          title: 'Reduce emissions from staff commuting by 60% by 2036.',
          climateImpact: 'This goal addresses emissions generated by daily travel to and from work, a routine but cumulative source of transportation-related emissions.',
          strategies: [
            {
              id: 'STM 2.1',
              title: 'Implement a comprehensive employee commuting program to reduce vehicle miles traveled through flexible work policies, mode shift incentives, and continuous performance monitoring.',
              priorityScore: 2,
              ghgRole: 'Indirect, Estimated 2,123 MT CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Human Resources, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 2,
              actions: [
                { id: 'STM 2.1.a', description: 'Conduct an updated staff commuter survey and repeat annually to monitor and track progress.' },
                { id: 'STM 2.1.b', description: 'Create a commuter benefits program that rewards staff who opt for low-carbon travel modes.' },
                { id: 'STM 2.1.c', description: 'Expand hybrid or remote work policies for eligible positions to reduce commuting days.' },
                { id: 'STM 2.1.d', description: 'Install EV chargers at all major municipal worksites for staff use.' }
              ]
            }
          ]
        },
        {
          id: 'STM 3',
          title: 'Reduce the number of registered petroleum-powered vehicles in Brookline by 40% from the current baseline by 2036.',
          climateImpact: 'This goal addresses climate impacts from private vehicle use by reducing reliance on internal combustion engine vehicles that burn fossil fuels.',
          strategies: [
            {
              id: 'STM 3.1',
              title: 'Increase accessibility to public EV charging stations on Town-owned property, through the expansion of charging in public parking lots, commercial districts, and curbside, to encourage and support adoption of EVs.',
              priorityScore: 1,
              ghgRole: 'Indirect, Estimated 33,500 CO₂e reduced',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$$',
              staffEstimate: 1,
              actions: [
                { id: 'STM 3.1.a', description: 'Monitor station usage and revenue to inform future station locations and charging fees.' },
                { id: 'STM 3.1.b', description: 'Prioritize the installation of DC fast chargers in high-traffic areas to serve residents and visitors.' },
                { id: 'STM 3.1.c', description: 'Increase utilization of existing public EV charging stations in municipal parking lots by permitting overnight parking at EV chargers that currently restrict it.' },
                { id: 'STM 3.1.d', description: 'Consider modification of EV charging rates to encourage off-peak usage.' },
                { id: 'STM 3.1.e', description: 'Leverage state and federal incentive programs and explore public-private partnerships to reduce installation and operating costs of EV charging stations.' }
              ]
            },
            {
              id: 'STM 3.2',
              title: 'Establish targets for EV charging stations on large residential and commercial properties, and provide resources and guidance to reduce financial, technical, and logistical barriers to EV charging access and adoption.',
              priorityScore: 2,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Building, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'STM 3.2.a', description: 'Collect data on EV charging infrastructure at private residential and commercial properties, including EV-ready and EVSE-installed spaces, to support baseline analysis, target-setting, and ongoing tracking.' },
                { id: 'STM 3.2.b', description: 'Establish EV charging targets for large residential and commercial properties.' },
                { id: 'STM 3.2.c', description: 'Engage large commercial property owners, prioritizing those in/near environmental justice neighborhoods, and partner with utilities to identify priority private parking lots for charging installations, addressing barriers such as interconnection and permitting.' },
                { id: 'STM 3.2.d', description: 'Promote adoption by providing targeted technical assistance to property owners and contractors on meeting Brookline’s EV-ready requirements, converting EV-ready spaces to EVSE-installed, and accessing state, federal and utility incentives.' },
                { id: 'STM 3.2.e', description: 'Collaborate with utilities to address grid capacity, transformer upgrades, and interconnection issues that can delay or complicate EVSE installations on private sites.' },
                { id: 'STM 3.2.f', description: 'Encourage private property owners to participate in EV charging share programs by making their chargers accessible to the public through networked platforms, supported by incentives and streamlined permitting.' }
              ]
            },
            {
              id: 'STM 3.3',
              title: 'Promote low-emission delivery and ride-sharing practices, and manage curbside activity to cut vehicle idling, congestion, and GHG emissions from freight and last-mile services.',
              priorityScore: 2,
              ghgRole: 'Indirect, Low Potential',
              coBenefits: ['Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'STM 3.3.a', description: 'Continue partnership with the Northeastern Intelligent Solutions to Urban Pollution for Equity and Resilience (iSUPER) program, to continue monitoring air quality in Brookline and utilize results of the study to inform decision-making on air quality improvements.' },
                { id: 'STM 3.3.b', description: 'Designate commercial loading zones in high-traffic areas to reduce double-parking and unnecessary idling by delivery vehicles.' },
                { id: 'STM 3.3.c', description: 'Partner with delivery companies, local businesses, and regional agencies to pilot cargo e-bikes and small electric delivery vehicles for last-mile deliveries in Brookline.' },
                { id: 'STM 3.3.d', description: 'Explore establishment of shared or consolidated drop-off hubs for multiple carriers to reduce redundant van trips and improve efficiency in commercial districts.' },
                { id: 'STM 3.3.e', description: 'Encourage the use of electric ride-hailing vehicles through company partnerships and expand convenient public and shared mobility alternatives for residents without access to private cars.' }
              ]
            }
          ]
        },
        {
          id: 'STM 4',
          title: 'Reduce per capita vehicle miles traveled from Brookline-registered vehicles by 25% by 2036.',
          climateImpact: 'This goal addresses transportation-related climate impacts by reducing the need for everyday driving.',
          strategies: [
            {
              id: 'STM 4.1',
              title: 'Increase micromobility Town-wide to improve accessibility and connectivity.',
              priorityScore: 3,
              ghgRole: 'Indirect, Estimated 6,900 CO₂e reduced',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources, Brookline Public Schools',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 1,
              actions: [
                { id: 'STM 4.1.a', description: 'Improve data on vehicle, pedestrian, and bicycle traffic by addressing missing or outdated data and adding automatic counters in known high-traffic pedestrian and bicycling areas.' },
                { id: 'STM 4.1.b', description: 'Provide safe and secure bike and scooter parking, prioritizing locations near high-activity commercial areas and key biking corridors.' },
                { id: 'STM 4.1.c', description: 'Continue to expand the network of Bluebike stations, particularly in densely populated areas and/or near marginalized communities.' },
                { id: 'STM 4.1.d', description: 'Improve pedestrian and bicycle infrastructure by expanding the all ages and abilities (AAA) network through a phased approach that incorporates both lower-cost quick-build improvements and longer-term capital projects in priority corridors, including near schools.' },
                { id: 'STM 4.1.e', description: 'Continue partnering with schools, employers, and community organizations to promote micromobility through programs such as Safe Routes to School, commuter challenges, and public education campaigns, while supporting and implementing relevant recommendations from prior Town transportation and commuter planning studies.' },
                { id: 'STM 4.1.f', description: 'Execute Complete Streets priority projects.' },
                { id: 'STM 4.1.g', description: 'Establish and enforce micro-mobility speed limits on shared use paths, bike lanes, and high-pedestrian areas to ensure safe coexistence with pedestrians, cyclists and micromobility riders.' }
              ]
            },
            {
              id: 'STM 4.2',
              title: 'Improve the reliability and accessibility of public transportation in Brookline.',
              priorityScore: 3,
              ghgRole: 'Indirect, Medium Potential',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources, Office of Diversity, Equity, Inclusion & Community Relations',
              startBy: '2035',
              costEstimate: '$$',
              staffEstimate: 2,
              actions: [
                { id: 'STM 4.2.a', description: 'Partner with the MBTA to improve transit access and reliability by enhancing bus and T-stops, publicizing real-time arrival information, and increasing service frequency on high-demand routes.' },
                { id: 'STM 4.2.b', description: 'Provide transit incentives such as discounted passes, employer-subsidized transit benefits, or free-ride programs for targeted groups like students, seniors, and low-income residents.' }
              ]
            },
            {
              id: 'STM 4.3',
              title: 'Support the goals of the Housing Production Plan to encourage high-density residential development, which reduces the need for transportation.',
              priorityScore: 2,
              ghgRole: 'Indirect, Estimated 23,100 CO₂e reduced',
              coBenefits: ['Equity', 'Economic', 'Infrastructure Resilience'],
              partneringDept: 'Sustainability & Natural Resources, Planning & Community Development',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 2,
              actions: [
                { id: 'STM 4.3.a', description: 'Increase the financial feasibility of creating or preserving affordable housing with local tax policies, such as a tax increment financing (TIF) approach to encourage affordable developments close to transit.' },
                { id: 'STM 4.3.b', description: 'Amend zoning bylaws to allow more high-density residential development near public transportation and micromobility infrastructure.' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'NSCW',
      name: 'Natural Systems and Community Well-Being',
      description: 'Goals and strategies under this category are designed to enhance environmental quality, ecological systems, and public health through urban forestry, urban heat mitigation, and air quality improvements.',
      goals: [
        {
          id: 'NSCW 1',
          title: 'Increase urban tree canopy coverage from baseline data in the 2020 LiDAR analysis.',
          climateImpact: 'This goal addresses climate impacts by expanding tree canopy, which helps absorb carbon dioxide while reducing local heat exposure.',
          strategies: [
            {
              id: 'NSCW 1.1',
              title: 'Plant 450 public trees annually and track subsequent changes to Town-wide canopy coverage.',
              priorityScore: 1,
              ghgRole: 'High Potential',
              coBenefits: ['Equity', 'Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Parks & Open Space, Sustainability & Natural Resources',
              startBy: '2029',
              costEstimate: '$$',
              staffEstimate: 2,
              actions: [
                { id: 'NSCW 1.1.a', description: 'Prioritize new tree planting in environmental justice neighborhoods, affordable and public housing parcels, areas with a high percentage of elderly residents, and public transit stops.' },
                { id: 'NSCW 1.1.b', description: 'Identify locations for additional tree planting where existing streetscapes and impermeable pavement could be converted to dig new planting pits and lawns.' },
                { id: 'NSCW 1.1.c', description: 'Define and map priority areas for successional planting where the canopy is mature and declining, and plant additional trees where feasible to ensure there is a new generation of trees that help diversify canopy age and species composition.' },
                { id: 'NSCW 1.1.d', description: 'Continue infill of plants and replacement of dead or storm-damaged trees, Town-wide.' },
                { id: 'NSCW 1.1.e', description: 'Execute street-specific planting recommendations for public ways.' },
                { id: 'NSCW 1.1.f', description: 'Procure new LiDAR data and analyze Town-wide tree canopy coverage every 5-6 years.' }
              ]
            },
            {
              id: 'NSCW 1.2',
              title: 'Protect existing private trees and incentivize planting of new trees on private properties.',
              priorityScore: 2,
              ghgRole: 'High Potential',
              coBenefits: ['Infrastructure Resilience'],
              partneringDept: 'Parks & Open Space, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'NSCW 1.2.a', description: 'Hire two additional full-time employees to support the implementation of the 2025 Tree Preservation bylaw.' },
                { id: 'NSCW 1.2.b', description: 'Amend the Brookline Wetlands Regulations to increase the protection of trees in areas under the jurisdiction of the Conservation Commission.' },
                { id: 'NSCW 1.2.c', description: 'Increase annual participation in the Front Yard Tree Program by 20% through promotion and participation incentives.' },
                { id: 'NSCW 1.2.d', description: 'Pilot an Adopt-a-Community-Tree program to promote connection to and engagement with the urban forest.' },
                { id: 'NSCW 1.2.e', description: 'Develop case studies to promote the planting of pocket forests on private property.' }
              ]
            }
          ]
        },
        {
          id: 'NSCW 2',
          title: 'Increase green infrastructure in municipal capital projects and achieve inclusion in 100 percent of eligible projects by 2030, maintaining that standard through 2036.',
          climateImpact: 'Integrating green infrastructure into municipal capital projects supports climate adaptation by improving stormwater management and reducing flood risk as precipitation becomes more intense and frequent.',
          strategies: [
            {
              id: 'NSCW 2.1',
              title: 'Develop and apply best standards and practices for green infrastructure in municipal capital improvement projects, including the use of bioswales, rain gardens, and other nature-based solutions that improve stormwater management, biodiversity, and climate resilience.',
              priorityScore: 2,
              ghgRole: 'High Potential',
              coBenefits: ['Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Parks & Open Space, Sustainability & Natural Resources, Water & Sewer',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 2,
              actions: [
                { id: 'NSCW 2.1.a', description: 'Establish a standardized green infrastructure evaluation process for all municipal capital projects to identify feasible measures.' },
                { id: 'NSCW 2.1.b', description: 'Require green infrastructure integration into project design where technically and financially appropriate, with priority for parks, schools, transit corridors, EJ neighborhoods, and heat-vulnerable areas.' },
                { id: 'NSCW 2.1.c', description: 'Ensure funding for maintenance and long-term performance of installed systems by requiring all municipal capital projects that include green infrastructure features to incorporate a dedicated maintenance budget line covering their expected life cycle and ongoing maintenance.' },
                { id: 'NSCW 2.1.d', description: 'Train municipal staff on proper maintenance of green infrastructure requiring annual servicing; contract out green infrastructure maintenance requiring more frequent maintenance.' }
              ]
            }
          ]
        },
        {
          id: 'NSCW 3',
          title: 'Increase site-scale green infrastructure and expanded canopy in private development.',
          climateImpact: 'This goal supports climate adaptation by expanding green infrastructure and tree canopy on private properties to reduce stormwater runoff and localized flooding.',
          strategies: [
            {
              id: 'NSCW 3.1',
              title: 'Adopt or amend zoning and general bylaws that require the integration of green infrastructure and low-impact development practices in projects of all use types, where soil and site conditions allow.',
              priorityScore: 2,
              ghgRole: 'High Potential',
              coBenefits: ['Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Sustainability & Natural Resources',
              startBy: '2032',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'NSCW 3.1.a', description: 'Review existing state and regional green infrastructure guidelines and adapt them into enforceable local requirements through zoning and site plan review.' },
                { id: 'NSCW 3.1.b', description: 'Amend Brookline’s bylaws to require site-scale features such as permeable pavement, bioswales, rain gardens, and tree canopy expansion in new development and major renovations.' },
                { id: 'NSCW 3.1.c', description: 'Establish a monitoring and compliance process so that approved projects deliver and maintain green infrastructure features as designed.' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'RAR',
      name: 'Resilience, Adaptation and Risk Reduction',
      description: 'The following goals and strategies help to prepare the community for, and reduce harm from, climate hazards through flooding mitigation, heat preparedness, emergency planning, backup power, risk mapping, and critical infrastructure upgrades.',
      goals: [
        {
          id: 'RAR 1',
          title: 'Increase climate resilience related to stormwater flood risk.',
          climateImpact: 'This goal supports climate adaptation by addressing increased stormwater flooding driven by more frequent and intense rainfall.',
          strategies: [
            {
              id: 'RAR 1.1',
              title: 'Implement relevant actions in the 2023 Hazard Mitigation Plan to mitigate flooding.',
              priorityScore: 2,
              ghgRole: 'Medium Potential',
              coBenefits: ['Equity', 'Economic', 'Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Engineering & Transportation, Parks and Open Space, Sustainability & Natural Resources, Public Health & Human Services, Water & Sewer',
              startBy: '2032',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'RAR 1.1.a', description: 'Improve stormwater management and decrease flood risk, through the completion and implementation of a stormwater management plan.' },
                { id: 'RAR 1.1.b', description: 'Update the Town’s Stormwater bylaw to clarify performance standards and streamline all requirements for green infrastructure and infiltration to protect the Town’s infrastructure.' },
                { id: 'RAR 1.1.c', description: 'Develop Town-wide drainage model to assess system vulnerabilities and areas susceptible to flooding, both in-land and adjacent to water bodies.' },
                { id: 'RAR 1.1.d', description: 'Create a Flood Hazard Identification Plan.' },
                { id: 'RAR 1.1.e', description: 'Implement stormwater management solutions in Municipal Capital Improvement Program.' },
                { id: 'RAR 1.1.f', description: 'Update sanitary sewer regulations that govern the use of public sewers, the installation and connection of building sewers, and the discharge of water and wastes into the public sewer system.' }
              ]
            }
          ]
        },
        {
          id: 'RAR 2',
          title: 'Improve urban heat island mitigation strategies on public properties.',
          climateImpact: 'This goal addresses the urban heat island effect, where paved surfaces and limited vegetation intensify heat as temperatures rise due to climate change.',
          strategies: [
            {
              id: 'RAR 2.1',
              title: 'Execute relevant actions in the 2023 Hazard Mitigation Plan to mitigate the urban heat island.',
              priorityScore: 1,
              ghgRole: 'Medium Potential',
              coBenefits: ['Equity', 'Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Highway & Sanitation, Public Health & Human Services, Sustainability & Natural Resources, Engineering & Transportation, Parks & Open Space',
              startBy: '2029',
              costEstimate: '$$',
              staffEstimate: 3,
              actions: [
                { id: 'RAR 2.1.a', description: 'Prioritize infrastructure on municipal properties that decreases the urban heat island effect, specifically in parking lots, such as shade awnings, tree plantings, and misting infrastructure, while also incorporating high reflective materials such as light color roofing materials, green roofs, light color permeable pavements that meet LEED standards.' },
                { id: 'RAR 2.1.b', description: 'Complete risk-mapping to identify vulnerable populations and geographic areas.' },
                { id: 'RAR 2.1.c', description: 'Identify populations lacking air conditioning and support the installation of extreme heat mitigation practices.' }
              ]
            },
            {
              id: 'RAR 2.2',
              title: 'Increase adaptation to extreme heat and poor air quality through targeted communications to vulnerable communities.',
              priorityScore: 2,
              ghgRole: 'High Potential',
              coBenefits: ['Equity', 'Public Health'],
              partneringDept: 'Office of Emergency Management, Public Health & Human Services, Sustainability & Natural Resources, Office of Diversity, Equity, Inclusion & Community Relations',
              startBy: '2032',
              costEstimate: '$',
              staffEstimate: 2,
              actions: [
                { id: 'RAR 2.2.a', description: 'Integrate air quality alerts into the Town’s existing emergency notification system.' },
                { id: 'RAR 2.2.b', description: 'Expand outreach campaign focused on the impacts of extreme heat and how to manage it.' }
              ]
            }
          ]
        },
        {
          id: 'RAR 3',
          title: 'Provide resilient power at critical facilities by installing solar-plus-storage systems at priority sites and addressing backup power needs.',
          climateImpact: 'This goal addresses climate driven power outages by strengthening energy resilience at critical facilities.',
          strategies: [
            {
              id: 'RAR 3.1',
              title: 'Utilize the 2023 Hazard Mitigation Plan to address backup power needs using non-carbon choices where viable.',
              priorityScore: 3,
              ghgRole: 'High Potential',
              coBenefits: ['Equity', 'Economic', 'Public Health', 'Infrastructure Resilience'],
              partneringDept: 'Building, Office of Emergency Management, Sustainability & Natural Resources',
              startBy: '2035',
              costEstimate: '$$$',
              staffEstimate: 2,
              actions: [
                { id: 'RAR 3.1.a', description: 'Assess critical power needs — including HVAC systems — at the Senior Center and three libraries.' },
                { id: 'RAR 3.1.b', description: 'Install back-up systems, such as solar-plus-storage systems supplemented by emergency generators where needed, to ensure designated warming and cooling centers remain operational during extreme weather events.' },
                { id: 'RAR 3.1.c', description: 'Incorporate solar-plus-storage systems for all facilities dedicated to public safety and primary shelter locations (public schools, fire stations, Municipal Service Center) to ensure energy continuity during outages while reducing long-term operating costs.' },
                { id: 'RAR 3.1.d', description: 'Partner with Eversource and National Grid to assess grid vulnerabilities during extreme weather and develop joint resilience plans, prioritizing upgrades for critical facilities and vulnerable neighborhoods.' }
              ]
            }
          ]
        },
        {
          id: 'RAR 4',
          title: 'Improve climate preparedness for individuals, families, and households by educating people on climate mitigation and adaptation, and equipping facilities to support residents in the event of a climate emergency.',
          climateImpact: 'This goal supports climate adaptation by strengthening household and community preparedness for extreme heat, flooding, and power outages driven by climate change.',
          strategies: [
            {
              id: 'RAR 4.1',
              title: 'Establish Climate Resiliency Hubs.',
              priorityScore: 1,
              ghgRole: 'High Potential',
              coBenefits: ['Equity', 'Public Health'],
              partneringDept: 'Office of Diversity, Equity, Inclusion & Community Relations, Office of Emergency Management, Public Health & Human Services, Sustainability & Natural Resources, Brookline Public Schools',
              startBy: '2029',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'RAR 4.1.a', description: 'Identify potential Climate Resiliency Hub locations in accessible community facilities, such as libraries and schools, prioritizing environmental justice neighborhoods and ensuring ADA compliance and transit access.' },
                { id: 'RAR 4.1.b', description: 'Partner with non-profits and service providers to deliver equitable, multilingual preparedness programming and (when necessary) emergency support, including food, medical supplies, and safety resources.' },
                { id: 'RAR 4.1.c', description: 'Develop a communications and funding plan to ensure residents know how to access the hubs and to secure sustainable resources for long-term operations and maintenance.' }
              ]
            },
            {
              id: 'RAR 4.2',
              title: 'Establish a team of Climate Ambassadors to educate residents on climate mitigation and adaptation solutions, and increase social connectedness.',
              priorityScore: 2,
              ghgRole: 'High Potential',
              coBenefits: ['Equity', 'Public Health'],
              partneringDept: 'Sustainability & Natural Resources, Office of Emergency Management, Public Health & Human Services, Office of Diversity, Equity, Inclusion & Community Relations',
              startBy: '2032',
              costEstimate: '$',
              staffEstimate: 3,
              actions: [
                { id: 'RAR 4.2.a', description: 'Develop a home electrification curriculum and train Climate Ambassadors to educate other residents about residential decarbonization.' },
                { id: 'RAR 4.2.b', description: 'Develop a disaster planning curriculum and train Climate Ambassadors to educate residents about climate resilience and preparedness.' },
                { id: 'RAR 4.2.c', description: 'Partner with Brookline schools, non-profits, faith-based institutions, and other local organizations to recruit and train Climate Ambassadors.' },
                { id: 'RAR 4.2.d', description: 'Create similar partnerships with local and small businesses, corporations, healthcare, childcare providers, landlords and management companies to disseminate relevant climate action resources.' },
                { id: 'RAR 4.2.e', description: 'Publish guidelines for camps, schools, and other institutions serving vulnerable populations on operations when there is poor air quality and/or extreme heat.' },
                { id: 'RAR 4.2.f', description: 'Increase multilingual education and awareness of environmental hazards caused by climate change, ways to address these hazards (ex. building retrofits), and information regarding emergency preparedness and resiliency.' }
              ]
            }
          ]
        },
        {
          id: 'RAR 5',
          title: 'Reduce municipal, residential, and commercial water use 15 percent by 2036 and adopt a drought preparedness plan.',
          climateImpact: 'This goal supports climate adaptation by reducing pressure on water systems as climate change increases drought risk, heat, and variability in precipitation.',
          strategies: [
            {
              id: 'RAR 5.1',
              title: 'Reduce water demand across municipal, residential, and commercial sectors through efficiency, reuse, and drought preparedness.',
              priorityScore: 3,
              ghgRole: 'Medium Potential',
              coBenefits: ['Infrastructure Resilience', 'Public Health', 'Economic'],
              partneringDept: 'Highway & Sanitation, Sustainability & Natural Resources, Water & Sewer, Parks & Open Space',
              startBy: '2035',
              costEstimate: '$$$',
              staffEstimate: 3,
              actions: [
                { id: 'RAR 5.1.a', description: 'Complete analysis of the irrigation maintenance and management system (IMMS), public water play areas, and inventory of rain sensors to determine opportunities for increased water efficiency.' },
                { id: 'RAR 5.1.b', description: 'Expand water efficiency programs at municipal facilities with the highest water usage by installing smart irrigation and instituting water reuse systems.' },
                { id: 'RAR 5.1.c', description: 'Pilot conversion of the Municipal Service Center wash bay into a reuse water system, and track water savings.' },
                { id: 'RAR 5.1.d', description: 'Implement the water reuse system at Fisher Hill Reservoir detention basin as an irrigation reservoir.' },
                { id: 'RAR 5.1.e', description: 'Promote residential and commercial water conservation through bulk purchasing and technical support for rain barrels, cisterns, water-efficient appliances, and greywater reuse where feasible.' },
                { id: 'RAR 5.1.f', description: 'Integrate water conservation into development review by updating zoning and site plan requirements to encourage permeable surfaces, drought-tolerant landscaping, and reuse systems in new construction.' },
                { id: 'RAR 5.1.g', description: 'Conduct public education campaigns in multiple languages to build awareness of water-saving practices and promote behavioral change.' }
              ]
            }
          ]
        }
      ]
    }
  ]
};

export const strategyStatuses = {
  'BE 1.1': 'Pre-Planning',
  'BE 1.2': 'In Development',
  'BE 1.3': 'Pre-Planning',
  'BE 1.4': 'Pre-Planning',
  'BE 2.1': 'In Development',
  'BE 2.2': 'Planning',
  'BE 2.3': 'Planning',
  'BE 3.1': 'Pre-Planning',
  'BE 3.2': 'Implementation',
  'BE 3.3': 'Pre-Planning',
  'BE 3.4': 'Planning',
  'BE 4.1': 'Enacted',
  'BE 4.2': 'Pre-Planning',
  'BE 4.3': 'Pre-Planning',
  'BE 5.1': 'Pre-Planning',
  'BE 6.1': 'Pre-Planning',
  'BE 7.1': 'Planning',
  'RCM 1.1': 'Implementation',
  'RCM 1.2': 'Planning',
  'RCM 1.3': 'Implementation',
  'RCM 1.4': 'Pre-Planning',
  'RCM 1.5': 'Pre-Planning',
  'RCM 1.7': 'Planning',
  'RCM 1.8': 'Pre-Planning',
  'STM 1.1': 'In Development',
  'STM 2.1': 'Pre-Planning',
  'STM 3.1': 'Implementation',
  'STM 3.2': 'Pre-Planning',
  'STM 3.3': 'Pre-Planning',
  'STM 4.1': 'Implementation',
  'STM 4.2': 'Pre-Planning',
  'STM 4.3': 'Pre-Planning',
  'NSCW 1.1': 'Implementation',
  'NSCW 1.2': 'In Development',
  'NSCW 2.1': 'Pre-Planning',
  'NSCW 3.1': 'Pre-Planning',
  'RAR 1.1': 'Planning',
  'RAR 2.1': 'Planning',
  'RAR 2.2': 'Implementation',
  'RAR 3.1': 'Pre-Planning',
  'RAR 4.1': 'Pre-Planning',
  'RAR 4.2': 'Pre-Planning',
  'RAR 5.1': 'Pre-Planning',
};

export const STATUS_IMAGE_MAP = {
  'Pre-Planning': '/images/status/pre-planning.png',
  'Planning': '/images/status/planning.png',
  'In Development': '/images/status/in-development.png',
  'Implementation': '/images/status/implementation.png',
  'Enacted': '/images/status/enacted.png',
};